// Update in Sidebar.tsx
import { X, LogOut, ChevronDown, Sun, Moon, Crown } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { logout, setLanguage } from "../store/userSlice";
import logo from "@packages/assets/logo_text_2.png";
import enFlag from "@packages/assets/en.png";
import trFlag from "@packages/assets/tr.png";
import deFlag from "@packages/assets/de.png";
import frFlag from "@packages/assets/fr.png";
import esFlag from "@packages/assets/es.png";

import {
  useLocation,
  Link,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "@packages/hooks/useTheme";
import { menuItemsFunction } from "@packages/utils";
import { Button } from "@core/button";
import { ProMemberModal } from "@features/pro-membership";
import { createAudit } from "@packages/api/madlenAPI";
import { MadlenAkademiModal } from "@features/madlen-akademi/MadlenAkademiModal";
import { MadlenAkademiButton } from "@features/madlen-akademi/MadlenAkademiButton";
import { getAuth } from "firebase/auth";

// Add type augmentation for window object
declare global {
  interface Window {
    handleLanguageChange?: (lang: string) => void;
  }
}

interface LanguageSelectorProps {
  selectedLanguage: string;
  onLanguageChange: (lang: string) => void;
}

const LanguageSelector = ({
  selectedLanguage,
  onLanguageChange,
}: LanguageSelectorProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  // Close popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Language options with flags
  const languages = [
    { code: "en", name: "English", flag: enFlag },
    { code: "tr", name: "Türkçe", flag: trFlag },
    { code: "de", name: "Deutsch", flag: deFlag },
    { code: "fr", name: "Français", flag: frFlag },
    { code: "es", name: "Español", flag: esFlag },
  ];

  const selectedLang =
    languages.find((lang) => lang.code === selectedLanguage) || languages[0];

  return (
    <div className="relative w-32" ref={popoverRef}>
      <Button
        onClick={() => setIsOpen(!isOpen)}
        rounded="sm"
        bgColor="hover:bg-gray-100 dark:hover:bg-gray-700"
        textColor="text-gray-600 dark:text-gray-300"
        size="sm"
        className="flex items-center justify-between gap-2 w-full"
      >
        <div className="flex items-center gap-2">
          <img
            src={selectedLang.flag}
            alt={selectedLang.name}
            className="w-5 h-5 rounded-full object-cover shadow-sm"
          />
          <span className="text-sm">{selectedLang.name}</span>
        </div>
        <ChevronDown
          size={14}
          className={`transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </Button>

      {isOpen && (
        <div className="absolute bottom-full right-0 mb-2 w-full bg-white dark:bg-gray-800 rounded-md shadow-lg overflow-hidden z-[150] border border-gray-100 dark:border-gray-700">
          <ul className="py-1">
            {languages.map((lang) => (
              <li key={lang.code}>
                <button
                  onClick={() => {
                    onLanguageChange(lang.code);
                    setIsOpen(false);
                  }}
                  className={`
                    flex items-center gap-2 w-full px-4 py-2 text-sm text-left transition-colors
                    ${
                      lang.code === selectedLanguage
                        ? "bg-primary-50 text-primary-600 dark:bg-primary-dark-800 dark:text-white"
                        : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                    }
                  `}
                >
                  <img
                    src={lang.flag}
                    alt={lang.name}
                    className="w-4 h-4 rounded-full object-cover shadow-sm"
                  />
                  <span>{lang.name}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

interface SidebarProps {
  isCollapsed: boolean;
  setIsCollapsed: (collapsed: boolean) => void;
}

const Sidebar = ({ isCollapsed, setIsCollapsed }: SidebarProps) => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [expandedSections, setExpandedSections] = useState<string[]>([]);
  const [isMobile, setIsMobile] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const menuItems = useMemo(
    () => menuItemsFunction(t, i18n.language),
    [t, i18n.language],
  );
  const [showProMemberModal, setShowProMemberModal] = useState(false);
  const [searchParams] = useSearchParams();
  const urlState = searchParams.get("state");
  const isPremium = useAppSelector(
    (state) => !!state.user.teacher?.preferences?.teacherProDate,
  );
  const userEmail = useAppSelector((state) => state.user.teacher?.email);
  const hasJoinedAcademy = useAppSelector(
    (state) => state.user.teacher?.hasJoinedAcademy,
  );
  const [akademiLoading, setAkademiLoading] = useState(false);
  const [showAkademiModal, setShowAkademiModal] = useState(false);
  const navRef = useRef<HTMLElement>(null);
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);
  const [hasReachedBottom, setHasReachedBottom] = useState(false);

  const isBilfen =
    window.location.hostname === "bilfen.madlen.io" ||
    userEmail?.includes("bilfen.k12.tr");
  const isIsik =
    window.location.hostname === "isik.madlen.io" ||
    userEmail?.includes("fmvisik.k12.tr");
  const isItugv =
    window.location.hostname === "itugvo.madlen.io" ||
    userEmail?.includes("itugvo.k12.tr");
  const isAyseAbla =
    window.location.hostname === "ayseabla.madlen.io" ||
    userEmail?.endsWith("ayseabla1@madlen.io") ||
    userEmail?.endsWith("ayseabla2@madlen.io") ||
    userEmail?.endsWith("ayseabla3@madlen.io");

  const isSev =
    window.location.hostname === "sev.madlen.io" ||
    userEmail?.includes("aci.k12.tr") ||
    userEmail?.includes("sev.org.tr") ||
    userEmail?.includes("sev.k12.tr") ||
    userEmail?.includes("sevizmir.k12.tr") ||
    userEmail?.includes("uaa.k12.tr") ||
    userEmail?.includes("tac.k12.tr") ||
    userEmail?.includes("sevtarsus.k12.tr") ||
    userEmail?.includes("sevkoleji.k12.tr");

  const isIelev =
    window.location.hostname === "ielev.madlen.io" ||
    userEmail?.includes("ielev.k12.tr");

  const isTed =
    window.location.hostname === "ted.madlen.io" ||
    window.location.hostname === "tedankara.madlen.io";

  const isNun =
    window.location.hostname === "nun.madlen.io" ||
    userEmail?.includes("nunokullari.com");

  const isMba =
    window.location.hostname === "mba.madlen.io" ||
    userEmail?.includes("mbaokullari.k12.tr");

  const isMsu =
    window.location.hostname === "msu.madlen.io" ||
    userEmail?.includes("msuokullari.k12.tr");

  const isMecOkullari =
    window.location.hostname === "mecokullari.madlen.io" ||
    userEmail?.includes("mecokullari.k12.tr");

  const isSahinkaya =
    window.location.hostname === "sahinkaya.madlen.io" ||
    userEmail?.includes("sahinkaya.k12.tr");

  const isYetev =
    window.location.hostname === "yetev.madlen.io" ||
    userEmail?.includes("yetev.org.tr") ||
    userEmail?.includes("progel.com.tr");

  const isMef =
    window.location.hostname === "mef.madlen.io" ||
    userEmail?.includes("mef.k12.tr");

  const isCihangir =
    window.location.hostname === "cihangir.madlen.io" ||
    userEmail?.includes("cihangir.k12.tr");

  const isHisar =
    window.location.hostname === "hisar.madlen.io" ||
    userEmail?.includes("hisarschool.k12.tr");

  const isSchool =
    isBilfen ||
    isIsik ||
    isItugv ||
    isAyseAbla ||
    isSev ||
    isIelev ||
    isTed ||
    isNun ||
    isMba ||
    isYetev ||
    isMsu ||
    isMecOkullari ||
    isMef ||
    isCihangir ||
    isHisar ||
    isSahinkaya;

  useEffect(() => {
    // Set initial value
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    checkIfMobile();

    // Add event listener for window resize
    window.addEventListener("resize", checkIfMobile);

    // Cleanup
    return () => window.removeEventListener("resize", checkIfMobile);
  }, []);

  useEffect(() => {
    if (isMobile) {
      setIsCollapsed(true);
    }
  }, [isMobile]);

  useEffect(() => {
    if (urlState) {
      setShowAkademiModal(true);
      handleAkademiConfirm();
    }
  }, [urlState]);

  // Add this effect to handle body scroll
  useEffect(() => {
    if (isMobile && !isCollapsed) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    // Cleanup when component unmounts
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isMobile, isCollapsed]);

  const handleLanguageChange = (lang: string) => {
    if (i18n.language === lang) return;
    dispatch(setLanguage(lang));
    i18n.changeLanguage(lang);
  };

  useEffect(() => {
    menuItems.forEach((item) => {
      if (item.subPages?.some((sub) => location.pathname.includes(sub.id))) {
        setExpandedSections((prev) =>
          prev.includes(item.id) ? prev : [...prev, item.id],
        );
      }
    });
  }, [location.pathname]);

  useEffect(() => {
    window.handleLanguageChange = handleLanguageChange;
  }, [handleLanguageChange]);

  // Update toggleSection function to collapse others
  const toggleSection = (id: string) => {
    // If clicking a non-collapsible section, close all expanded sections
    if (!menuItems.find((item) => item.id === id)?.subPages) {
      console.log("non collapsible section");
      setExpandedSections([]);
      return;
    }

    setExpandedSections((prev) => {
      if (prev.includes(id)) {
        // If clicking on already expanded section, just close it
        return prev.filter((item) => item !== id);
      } else {
        // If clicking on a new section, close others and open this one
        return [id];
      }
    });
  };

  const handleLogout = () => {
    dispatch(logout());
  };

  // Helper function to check if path meets condition for prevention
  const shouldPreventNavigation = () => {
    const lastChatMessageCount = parseInt(
      localStorage.getItem("lastChatMessageCount") || "0",
      10,
    );
    return (
      lastChatMessageCount === 1 &&
      location.pathname.startsWith("/assistants/new/empty")
    );
  };

  // Function to handle confirmed navigation to Madlen Akademi
  const handleAkademiConfirm = async () => {
    setAkademiLoading(true);
    await createAudit({
      actionName: "Madlen Akademi",
      extraDetails: {
        category: "Madlen Akademi",
        action: "Madlen Akademi",
        label: "Madlen Akademi",
      },
    });
    let jwt = JSON.parse(localStorage.getItem("userState") || "{}").token;
    try {
      const jwtPayload = JSON.parse(window.atob(jwt.split(".")[1]));
      const exp = jwtPayload.exp;
      if (exp < Date.now() / 1000) {
        // invalid token
        const auth = getAuth();

        // Wait for auth to initialize and get current user
        await new Promise((resolve) => {
          const unsubscribe = auth.onAuthStateChanged((user) => {
            unsubscribe();
            resolve(user);
          });
        });

        const user = auth.currentUser;
        if (!user) {
          throw new Error("No user found");
        }
        console.log("refreshing token for user");
        const newToken = await user.getIdToken(true); // Force refresh
        jwt = newToken;
      }
    } catch (error) {
      console.error(error);
    }
    if (urlState) {
      window.location.href = `https://akademi.madlen.io/oauth2/callback?code=${btoa(jwt)}&state=${urlState}`;
      navigate(`/dashboard`, { replace: true });
    } else {
      window.location.href = `https://akademi.madlen.io`;
    }
    setAkademiLoading(false);
  };

  // Handle scroll events to check position
  const handleScroll = useCallback(() => {
    if (navRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = navRef.current;
      // Consider "reached bottom" when within 20px of the bottom
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 20;
      setHasReachedBottom(isAtBottom);
    }
  }, []);

  // Check if nav is scrollable
  useEffect(() => {
    const checkScrollable = () => {
      if (navRef.current) {
        const isScrollable =
          navRef.current.scrollHeight > navRef.current.clientHeight;
        setShowScrollIndicator(isScrollable);

        // Check initial scroll position
        handleScroll();
      }
    };

    checkScrollable();
    window.addEventListener("resize", checkScrollable);

    return () => {
      window.removeEventListener("resize", checkScrollable);
    };
  }, [menuItems, expandedSections]);

  // Handle scroll events to update indicator visibility
  useEffect(() => {
    const navElement = navRef.current;
    if (!navElement) return;

    navElement.addEventListener("scroll", handleScroll);

    // Initial check
    handleScroll();

    return () => {
      navElement.removeEventListener("scroll", handleScroll);
    };
  }, [handleScroll]);

  // Handle scroll to bottom
  const scrollToBottom = () => {
    if (navRef.current) {
      navRef.current.scrollTo({
        top: navRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  };

  return (
    <>
      <div
        className={`
          sidebar-mobile fixed top-0 left-0 h-[100dvh] bg-white shadow-xl transition-all duration-300 z-[180]
          ${isCollapsed ? "-translate-x-full !w-0 touch-none pointer-events-none select-none opacity-0" : "translate-x-0"} 
          w-full md:w-64 lg:sticky lg:top-0 lg:z-0
          dark:bg-gray-800 dark:text-white flex flex-col
        `}
      >
        <div className="flex flex-col h-full">
          <div className="border-b pb-1 dark:border-gray-700 flex justify-center items-center relative">
            {!isCollapsed && (
              <Button
                onClick={() => setIsCollapsed(true)}
                bgColor="hover:bg-gray-100 dark:hover:bg-gray-700"
                rounded="lg"
                className="block lg:hidden absolute top-0 left-0 h-full"
                size="md"
              >
                <X size={24} />
              </Button>
            )}
            <Link to="/" onClick={() => isMobile && setIsCollapsed(true)}>
              <img src={logo} alt="Madlen Logo" className="w-36 mx-auto" />
            </Link>
          </div>

          {/* Menu Items - Added overflow-y-auto */}
          <nav
            ref={navRef}
            className="flex-1 p-4 overflow-y-auto min-h-0 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-transparent hover:[&::-webkit-scrollbar-thumb]:bg-gray-300/50 dark:hover:[&::-webkit-scrollbar-thumb]:bg-gray-500/50 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:transition-colors relative"
            style={{
              scrollbarWidth: "thin",
              scrollbarColor: "transparent transparent",
            }}
            onScroll={handleScroll}
          >
            <ul className="space-y-2">
              {menuItems.map((item) => (
                <li key={item.id}>
                  <div className="flex flex-col">
                    {item.id === "assistants/new/essay-grader" ? (
                      <Button
                        fullWidth
                        rounded="lg"
                        bgColor={`${
                          location.pathname.includes("/essay-grading")
                            ? "bg-primary-50 dark:bg-primary-dark-800 "
                            : "hover:bg-gray-50 dark:hover:bg-gray-700"
                        }`}
                        textColor={`${
                          location.pathname.includes("/essay-grading")
                            ? "text-primary-500 dark:text-white"
                            : "text-gray-600 dark:text-gray-300"
                        }`}
                        className="select-none"
                        size="md"
                        onClick={() => {
                          if (isMobile) setIsCollapsed(true);
                          navigate("/essay-grading");
                        }}
                        data-testid="essay-grader-sidebar-button"
                      >
                        <div className="flex items-center flex-1">
                          <item.icon size={20} />
                          <span className="ml-3 mr-2 text-left font-[400]">
                            {item.text}
                          </span>
                        </div>
                      </Button>
                    ) : (
                      <Link
                        to={"/" + item.id}
                        onClick={(e) => {
                          if (
                            shouldPreventNavigation() &&
                            item.id === "assistants/new/empty"
                          ) {
                            e.preventDefault();
                          } else {
                            toggleSection(item.id);
                            // Only collapse the sidebar on mobile if this item doesn't have subpages
                            if (isMobile && !item.subPages) {
                              setIsCollapsed(true);
                            }
                          }
                        }}
                        className={`
                        w-full flex items-center px-4 py-3 rounded-lg transition-colors select-none
                        ${
                          location.pathname.includes(item.id) &&
                          !item.subPages?.some((sub) =>
                            location.pathname.includes(sub.id),
                          )
                            ? "bg-primary-50 text-primary-500 dark:bg-primary-dark-800 dark:text-white"
                            : "text-gray-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700"
                        }
                      `}
                      >
                        <div className="flex items-center flex-1">
                          <item.icon size={20} />
                          <span className="ml-3 mr-2">{item.text}</span>
                        </div>
                        {item.subPages && (
                          <ChevronDown
                            size={18}
                            className={`transition-transform duration-200 ${
                              expandedSections.includes(item.id)
                                ? "rotate-180"
                                : ""
                            }`}
                          />
                        )}
                      </Link>
                    )}
                    {item.subPages && (
                      <ul
                        className={`
            ml-2 mt-2 space-y-2 overflow-hidden transition-all duration-200
            ${expandedSections.includes(item.id) ? "max-h[550px]" : "max-h-0"}
          `}
                      >
                        {item.subPages.map((subPage) => {
                          if (subPage.id === "assistants/new/essay-grader") {
                            return null;
                          }

                          const longestMatch = item.subPages
                            .filter((sp) => location.pathname.includes(sp.id))
                            .sort((a, b) => b.id.length - a.id.length)[0]?.id;

                          // Check if subpage has a path with query parameters
                          const hasQueryParams =
                            "path" in subPage && subPage.path?.includes("?");

                          // For query parameter based routes, check if both path and query params match
                          const isActive = hasQueryParams
                            ? location.pathname ===
                                subPage.path?.split("?")[0] &&
                              location.search ===
                                "?" + subPage.path?.split("?")[1]
                            : "id" in subPage &&
                              location.pathname.includes((subPage as any).id) &&
                              (subPage as any).id === longestMatch;

                          // Fix TS error: Assume subPage has id if no path
                          const subPageId =
                            "id" in subPage ? (subPage as any).id : "";
                          const linkPath =
                            "path" in subPage ? subPage.path : "/" + subPageId;

                          return (
                            <li key={subPageId}>
                              <Link
                                to={linkPath ?? "/"}
                                onClick={(e) => {
                                  const isEmptyAssistantPath =
                                    linkPath?.startsWith(
                                      "/assistants/new/empty",
                                    );

                                  if (
                                    shouldPreventNavigation() &&
                                    isEmptyAssistantPath
                                  ) {
                                    e.preventDefault();
                                  } else {
                                    if (isMobile) setIsCollapsed(true);
                                  }
                                }}
                                className={`
    w-full flex items-center space-x-3 pl-2 py-2 rounded-lg transition-colors text-sm select-none ${
      subPage.color
    }
    ${
      isActive &&
      "bg-primary-50 text-primary-500 dark:bg-primary-dark-800 dark:text-white"
    }
  `}
                              >
                                <subPage.icon size={20} />
                                <span>{subPage.text}</span>
                              </Link>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                </li>
              ))}
            </ul>

            {/* Scroll Indicator Button */}
            {showScrollIndicator && !hasReachedBottom && (
              <button
                onClick={scrollToBottom}
                className="absolute bottom-2 right-2 w-8 h-8 rounded-full bg-white dark:bg-gray-700 shadow-md border border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600 flex items-center justify-center transition-all duration-200 focus:outline-none z-10 animate-pulse hover:animate-bounce"
                aria-label="Scroll to bottom"
                title={t("sidebar.scrollToBottom", "Scroll to view more items")}
              >
                <ChevronDown
                  size={18}
                  className="text-gray-600 dark:text-gray-300"
                />
              </button>
            )}
          </nav>

          {/* Madlen Akademi CTA Section */}
          {/* <div className="px-4 py-3 border-t dark:border-gray-700 akademi-button z-10 bg-white dark:bg-gray-800 w-full flex-shrink-0 min-h-[50px]">
            <MadlenAkademiButton
              handleAkademiConfirm={handleAkademiConfirm}
              akademiLoading={akademiLoading}
              highlight={!hasJoinedAcademy}
            />
          </div> */}
          <div
            className={`${isMobile ? "sticky bottom-0 z-20 bg-white dark:bg-gray-800 border-t dark:border-gray-700 px-4 py-3" : "px-4 py-3 border-t dark:border-gray-700 akademi-button z-10 bg-white dark:bg-gray-800 w-full flex-shrink-0 min-h-[50px]"} `}
          >
            <MadlenAkademiButton
              handleAkademiConfirm={handleAkademiConfirm}
              akademiLoading={akademiLoading}
              highlight={!hasJoinedAcademy}
            />
          </div>

          {/* Bottom section - Added sticky positioning */}
          {!isCollapsed && (
            <div className="sticky bottom-0 bg-white dark:bg-gray-800 border-t dark:border-gray-700 z-20">
              <div className="p-4 flex items-center justify-between">
                {(isPremium || isSchool) && (
                  <div>
                    <div className="flex items-center justify-between relative z-10 w-full">
                      <div className="bg-amber-50 dark:bg-amber-900/30  select-none cursor-pointer flex flex-row items-center mx-auto space-x-2 px-3 rounded-full border border-amber-200 dark:border-amber-700/50 shadow-sm">
                        <span className="font-medium text-xs my-1 shadow-sm text-amber-800 dark:text-amber-300 flex items-center">
                          <Crown size={14} className="mr-1 text-amber-500" />
                          Madlener Pro
                        </span>
                      </div>
                    </div>
                  </div>
                )}
                {!isPremium && !isSchool && (
                  <div>
                    <Button
                      onClick={() => setShowProMemberModal(true)}
                      fullWidth
                      rounded="xl"
                      size="md"
                      className="relative overflow-hidden group transform hover:scale-[1.02] transition-all duration-200 shadow-md hover:shadow-lg"
                      bgColor="bg-gradient-to-r from-amber-400 to-amber-500 hover:from-amber-500 hover:to-amber-600 dark:from-amber-500 dark:to-amber-600 dark:hover:from-amber-600 dark:hover:to-amber-700"
                      textColor="text-white"
                    >
                      <div className="absolute inset-0 bg-white/20 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-in-out"></div>
                      <div className="flex items-center justify-center gap-2">
                        <Crown size={16} className="text-white animate-pulse" />
                        <span className="font-bold text-xs whitespace-nowrap">
                          {t("sidebar.becomePro", "Become a Pro Member")}
                        </span>
                      </div>
                    </Button>
                  </div>
                )}
                <Button
                  onClick={toggleTheme}
                  className="relative w-14 h-7 hover:shadow-lg hover:scale-105"
                  rounded="full"
                  aria-label="Toggle theme"
                  bgColor="dark:bg-gray-700 bg-gray-200"
                >
                  <div
                    className={`
                      absolute w-6 h-6 rounded-full bg-white shadow-md transform transition-all duration-300 ease-in-out
                      flex items-center justify-center
                      ${
                        theme === "dark"
                          ? "translate-x-[14px] rotate-[360deg]"
                          : "translate-x-[-14px] rotate-0"
                      }
                    `}
                  >
                    {theme === "dark" ? (
                      <Moon size={14} className="text-indigo-600" />
                    ) : (
                      <Sun size={14} className="text-amber-500" />
                    )}
                  </div>
                  <span className="sr-only">Toggle theme</span>
                </Button>
              </div>
              <div className="flex items-center justify-between gap-2 pb-4 px-4 relative z-30">
                <div className="flex items-center justify-center h-full">
                  <LanguageSelector
                    selectedLanguage={i18n.language}
                    onLanguageChange={handleLanguageChange}
                  />
                </div>
                <Button
                  onClick={handleLogout}
                  textColor="text-gray-600 hover:text-red-600 dark:text-gray-300 dark:hover:text-red-400"
                  size="md"
                  className="flex items-center p-0"
                >
                  <LogOut size={18} />
                  <span className="text-sm">{t("sidebar.logout")}</span>
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Pro Member Modal */}
      {showProMemberModal && (
        <ProMemberModal
          isOpen={showProMemberModal}
          onClose={() => setShowProMemberModal(false)}
        />
      )}

      {/* Madlen Akademi Modal */}
      <MadlenAkademiModal isOpen={showAkademiModal} />
    </>
  );
};

export default Sidebar;
